import { useState } from "react";
import { Card, Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton } from "ui-components/IconButton";
import { Success, Error_ } from "ui-components/Alert";
import { AddButton } from "ui-components/IconButton";
import { getZoneList } from "services/data.internal.service";
import { DelButton } from "ui-components/IconButton";
import { getZoneStatusCount } from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { MessageDialog } from "ui-components/Alert";
import { deactivateZone } from "services/data.internal.service";
import { activeZone } from "services/data.internal.service";
import { ActiveButton } from "ui-components/IconButton";
import { TableCard } from "ui-components/Layout";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function ZoneList() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["zone-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const addPermission =
    allowAll ||
    (Boolean(permissions["Zone"]) && permissions["Zone"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Zone"]) && permissions["Zone"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Zone"]) &&
      permissions["Zone"].findIndex((b) => b === "deactivate") !== -1);
  const addLocationPermission =
    allowAll ||
    (Boolean(permissions["Location"]) &&
      permissions["Location"].findIndex((b) => b === "new") !== -1);

  useQuery({
    queryKey: ["zone-list", { customerCode, active }],
    queryFn: () => getZoneList({ customerCode, active }),
    onSuccess: (data) => setData(data.data.data),
    enabled: Boolean(customerCode),
    onError,
  });

  useQuery({
    queryKey: ["zone-list", "status", { customerCode, active }],
    queryFn: () => getZoneStatusCount({ customerCode }),
    onSuccess: (data) => setTabs(data.data.data),
    enabled: Boolean(customerCode),
    onError,
  });

  const delMutation = useMutation(deactivateZone, { onSuccess, onError });
  const activeMutation = useMutation(activeZone, { onSuccess, onError });

  const onEdit = (req) => navigate("/setup/zone/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.zoneID}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.zoneID}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const onAdd = (req) => navigate("/setup/location/add", { state: req });

  const columns = [
    { header: "Zone ID", accessorKey: "zoneID" },
    { header: "Warehouse Code", accessorKey: "warehouseCode" },
    { header: "Zone Type", accessorKey: "zoneType" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { warehouseCode, zoneID } = row.original;
        const state = { warehouseCode, zoneID };
        if (active) {
          return (
            <>
              <EditButton
                title="edit zone"
                disabled={!editPermission}
                onClick={() => onEdit(state)}
              />
              <DelButton
                title="deactivate zone"
                disabled={!deactivatePermission}
                onClick={() => onDelete(state)}
              />
              <AddButton
                title="create location"
                disabled={!addLocationPermission}
                onClick={() => onAdd(state)}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate zone"
              disabled={!editPermission}
              onClick={() => onActive(state)}
            />
          );
        }
      },
    },
  ];
  const rows = data.map((d) => {
    const { zoneID, warehouseCode, zoneType } = d;
    return {
      zoneID,
      warehouseCode,
      zoneType,
    };
  });
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="flex-start" alignItems="flex-start" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/setup/zone/add")}
          >
            <Icon>add</Icon>&nbsp;zone
          </MDButton>
          <MDButton
            sx={{ marginLeft: "1rem" }}
            variant="outlined"
            color="dark"
            disabled={!addPermission}
            onClick={() => navigate("/setup/zone/upload")}
          >
            <Icon>upload</Icon> &nbsp;upload
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default ZoneList;
